import { useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { EmptystateCodeFileColored, EmptystateVariablesColored } from "components/icons/generated";
import Box from "ds/components/Box";
import CardCollapsible from "ds/components/Card/Collapsible";
import useAutoExpandOnValueChange from "ds/components/Card/Collapsible/useAutoExpandOnValueChange";
import Counter from "ds/components/Counter";
import EmptyState from "ds/components/EmptyState";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import SegmentedControl from "ds/components/SegmentedControl";
import { useToggle } from "hooks/useToggle";
import useTypedContext from "hooks/useTypedContext";

import NewContextFooter from "../Footer";
import NewContextVariableForm from "../ContextVariables/AddForm";
import NewContextVariable from "../ContextVariable";
import NewContextMountedFileForm from "../ContextMountedFiles/AddForm";
import NewContextMountedFile from "../ContextMountedFile";
import { ContextFormContext } from "../context";
import { ContextFormFields } from "../types";
import { useSaveContext } from "../hooks/useSaveContext";
import { ContextCreationWizardStepName } from "../constants";

enum EnvironmentType {
  Variables = "variables",
  MountedFiles = "mounted-files",
}

const options = [
  { label: "Variables", value: EnvironmentType.Variables },
  { label: "Mounted files", value: EnvironmentType.MountedFiles },
];

const NewContextSetupEnvironment = () => {
  const { goToNextStep } = useTypedContext(ContextFormContext);
  const [configType, setConfigType] = useState(EnvironmentType.Variables);
  const { handleSubmit } = useFormContext<ContextFormFields>();
  const [isVariablesCollapsed, toggleVariables] = useToggle(true);
  const [isMountedFilesCollapsed, toggleMountedFiles] = useToggle(true);

  const {
    fields: attachedVariables,
    append: appendVariable,
    update: updateVariable,
    remove: removeVariable,
  } = useFieldArray<ContextFormFields, "attachedVariables">({
    name: "attachedVariables",
  });

  const {
    fields: attachedMountedFiles,
    append: appendMountedFile,
    update: updateMountedFile,
    remove: removeMountedFile,
  } = useFieldArray<ContextFormFields, "attachedMountedFiles">({
    name: "attachedMountedFiles",
  });

  useAutoExpandOnValueChange(attachedVariables.length, toggleVariables);
  useAutoExpandOnValueChange(attachedMountedFiles.length, toggleMountedFiles);

  const [saveContext, isSavingContext] = useSaveContext(ContextCreationWizardStepName.Environment);

  const continueButtonAnalyticsData = {
    variablesCount: attachedVariables.length,
    mountedFilesCount: attachedMountedFiles.length,
  };

  return (
    <>
      <FullScreenModalBody>
        <FullScreenModalBodyHeader
          title="Setup environment (optional)"
          description="Adding environment variables and mounted files to a context at this point is optional. You can still do that later after creating the context."
        />

        <SegmentedControl value={configType} onChange={setConfigType} options={options} />

        <Box margin="x-large 0 large 0">
          {configType === EnvironmentType.Variables && (
            <NewContextVariableForm onSave={appendVariable} />
          )}
          {configType === EnvironmentType.MountedFiles && (
            <NewContextMountedFileForm onSave={appendMountedFile} />
          )}
        </Box>

        {configType === EnvironmentType.Variables && (
          <CardCollapsible
            ariaLevel={2}
            ariaLabel="Added variables"
            title="Added variables"
            isCollapsed={isVariablesCollapsed}
            onToggle={toggleVariables}
            titleSuffix={<Counter count={attachedVariables.length} />}
            contentGap="medium"
          >
            {attachedVariables.length === 0 ? (
              <EmptyState
                padding="large 0"
                icon={EmptystateVariablesColored}
                caption="Context does not have any environment variables."
              />
            ) : (
              <>
                {attachedVariables.map((item, index) => (
                  <NewContextVariable
                    key={item.id}
                    item={item}
                    index={index}
                    onSave={updateVariable}
                    onRemove={removeVariable}
                  />
                ))}
              </>
            )}
          </CardCollapsible>
        )}

        {configType === EnvironmentType.MountedFiles && (
          <CardCollapsible
            ariaLevel={2}
            ariaLabel="Added files"
            title="Added files"
            isCollapsed={isMountedFilesCollapsed}
            onToggle={toggleMountedFiles}
            titleSuffix={<Counter count={attachedMountedFiles.length} />}
            contentGap="medium"
          >
            {attachedMountedFiles.length === 0 ? (
              <EmptyState
                padding="large 0"
                icon={EmptystateCodeFileColored}
                caption="Context does not have any mounted files."
              />
            ) : (
              <>
                {attachedMountedFiles.map((item, index) => (
                  <NewContextMountedFile
                    key={item.id}
                    item={item}
                    index={index}
                    onSave={updateMountedFile}
                    onRemove={removeMountedFile}
                  />
                ))}
              </>
            )}
          </CardCollapsible>
        )}
      </FullScreenModalBody>

      <NewContextFooter
        onContinueClick={handleSubmit(goToNextStep)}
        onCreateContextClick={handleSubmit(saveContext)}
        isCreatingContext={isSavingContext}
        continueClickAnalyticsProps={continueButtonAnalyticsData}
      />
    </>
  );
};

export default NewContextSetupEnvironment;
